<template>
  <div class="w-full">
    <table class="w-full text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <th
            colspan="2"
            class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          />

          <th
            colspan="3"
            class="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50"
          >
            Quantities
          </th>

          <th
            colspan="3"
            class="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Rates
          </th>

          <th
            colspan="3"
            class="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50"
          >
            Performance
          </th>

          <th
            colspan="3"
            class="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Forecast
          </th>
        </tr>

        <tr class="bg-gray-100 border-b border-gray-200">
          <th class="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
            WBS
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The tender amount is based on the quantity and cost per unit"
          >
            Unit
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The final quantity that includes the quantity as updated by the user"
          >
            Tender
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The unit of measure for the combined items"
          >
            To Date
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The cost per unit of work the formula is [Tender Amount/Qty]"
          >
            To Complete
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The cost per unit of work the formula is [To Date Amount/Qty]"
          >
            Tender
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The cost per unit of work the formula is [Cost Per Unit to date x (To Complete Production Rate / To Date Production Rate)]"
          >
            To Date
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The cost value based on the number of quantities completed"
          >
            To Complete
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The earned value based on the number of quantities completed"
          >
            Cost
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The difference between the cost and earned amounts"
          >
            Earned Amount
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The cost to complete the works. The formula is [cost per unit x the Qty to Complete]"
          >
            Variance
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The forecast final cost. The formula is [Cost To Complete + Cost Amount To Date]"
          >
            Cost
          </th>

          <th
            class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            title="The forecast final cost. The formula is [Cost To Complete + Cost Amount To Date]"
          >
            Value
          </th>

          <th class="px-2 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
            Profit/Loss
          </th>
        </tr>
      </thead>

      <tbody class="bg-white divide-y divide-gray-200">
        <template
          v-for="wbsCode in wbsCodes"
          :key="wbsCode.id"
        >
          <WbsCodeListRow
            v-if="toCompleteTotals.get(wbsCode.id)"
            :wbs-code="wbsCode"
            :to-complete="toCompleteTotals.get(wbsCode.id)"
          />
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput } from '@/helpers';
import { safeDenominator } from '@/helpers';
import { useDailyQuantitiesStore } from '@/store/dailyQuantities';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { route } from '@/ziggy-shim';
import { computed, ref, watch } from 'vue';
import WbsCodeListRow from './WbsCodeListRow.vue';

const props = defineProps<{
  projectId: number;
}>();

const wbsCodesStore = useWbsCodesStore();
const dailyQuantitiesStore = useDailyQuantitiesStore();
const fetchDailyQuantitiesAction = useStoreApiAction(dailyQuantitiesStore.toComplete);

const dailyQuantitiesForm = useFormData({
  to_date: dateForDateInput(new Date()),
});

const wbsTotals = ref<Map<string, { totalHours: number; totalCost: number; totalQuantity: number; totalDays: number }>>(
  new Map(),
);

const toCompleteTotals = computed(() => {
  const toCompleteMap = new Map<number, CostAtCompletion>();

  for(const [key, value] of wbsTotals.value) {
    const wbsCode = wbsCodesStore.models.find(parseInt(key));

    if(wbsCode) {
      const toDateUnitCost = value.totalCost / safeDenominator(value.totalQuantity);
      const toDateProductionRate = value.totalQuantity / safeDenominator(value.totalDays);
      const toDateEarnedAmount = value.totalQuantity * wbsCode.unitCost;
      const toCompleteQuantity = (wbsCode.final_quantity || wbsCode.tender_quantity) - value.totalQuantity;
      const toCompleteProductionRate = wbsCode.to_complete_production_rate || toDateProductionRate;

      const toCompleteUnitCost = toDateUnitCost /
        safeDenominator(toCompleteProductionRate / safeDenominator(toDateProductionRate));

      const toCompleteCost = toCompleteUnitCost * toCompleteQuantity;
      const forecastCost = value.totalCost + toCompleteCost;

      const profit = (value.totalCost + toCompleteCost) === 0 ?
        0 :
        (wbsCode.tender_quantity * wbsCode.unitCost) - (value.totalCost + toCompleteCost);

      toCompleteMap.set(wbsCode.id, {
        toDateQuantity: value.totalQuantity,
        toDateDays: value.totalQuantity / toDateProductionRate,
        toDateProductionRate,
        toDateUnitCost,
        toDateCost: value.totalCost,
        toDateEarnedAmount,
        toDateVariance: toDateEarnedAmount - value.totalCost,
        toCompleteQuantity,
        toCompleteProductionRate,
        toCompleteUnitCost,
        toCompleteDays: toCompleteQuantity / safeDenominator(toCompleteProductionRate),
        toCompleteCost,
        forecastCost,
        profit,
      });
    }
  }

  return toCompleteMap;
});

const wbsCodes = computed(() => {
  const sortedCodes = wbsCodesStore.models.where('project_id', props.projectId).get().sort((a, b) => {
    return toCompleteTotals.value.get(b.id)?.toDateVariance - toCompleteTotals.value.get(a.id)?.toDateVariance;
  });

  if(sortedCodes.length < 10) {
    return sortedCodes;
  }

  return sortedCodes.slice(0, 5).concat(sortedCodes.slice(-5));
});

const fetchDailyQuantities = () => {
  fetchDailyQuantitiesAction.request(props.projectId, dailyQuantitiesForm.form).then((data) => {
    wbsTotals.value = data;
  }).catch((error) => {
    console.log(error);
  });
};

watch(() => props.projectId, () => {
  fetchDailyQuantities();
}, { immediate: true });
</script>

<style scoped></style>
